import {
  Card,
  Carousel,
  Divider,
  Grid,
  GridGallery,
  Heading,
  ProductImageGallery,
  ReviewCard,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import {
  getFirstStoryblokParagraph,
  isStoryblokRichTextPresent,
} from '@utils/renderStoryblokRichText';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import SbEditable from 'storyblok-react';
import { render } from 'storyblok-rich-text-react-renderer-ts';

import { WORK_PAGE_TYPE } from '../../constants';
import {
  PRODUCT_TAG_MEDIUM_COLLECTIBLE,
  PRODUCT_TAG_MEDIUM_NFT,
  PRODUCT_TAG_MEDIUM_PRINT,
  PRODUCT_TAG_MEDIUM_SCULPTURE,
  PRODUCT_TAG_MEDIUM_STUDIO_WORKS,
} from '../../constants/product';
import { DrawByProductId, DrawGateway } from '../../features/draws/types';
import { ImageSizes } from '../../helpers/images';
import { pathFor } from '../../helpers/links';
import {
  getProductRef,
  getProductStatus,
  presentedPurchasingState,
  purchasingState,
} from '../../helpers/purchasing';
import { useIsSubscribed } from '../../hooks/subscription/useIsSubscribed';
import { useDraw } from '../../hooks/useDraw';
import { useDrawStage } from '../../hooks/useDrawStage';
import { useIsPreOrder } from '../../hooks/useIsPreOrder';
import { useProducts } from '../../hooks/useProducts';
import { usePurchase } from '../../providers/PurchaseProvider';
import { MultiMediaClickOptions } from '../../types/Analytics';
import { AugmentedArtistCard } from '../../types/Artists';
import { ProductGateway } from '../../types/AvantArteProduct';
import {
  GeneratedReview,
  GeneratedSubscriptionType,
} from '../../types/generated';
import { SeriesStoryblok } from '../../types/generated-storyblok';
import { ProductStructure } from '../../types/Products';
import {
  ProductPurchaseDetails,
  ProductPurchasingController,
  PurchasingState,
} from '../../types/Purchasing';
import {
  getStandardProductProperties,
  TrackCarouselSlide,
  TrackEvent,
  TrackLinkClicked,
  TrackMultiMediaClick,
  TrackWorkPageView,
  transformProductToEventObject,
} from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { AnalyticsObjectProvider } from '../../utils/analytics/object';
import {
  getArtistTileOverline,
  renderArtistsNamesFromPageContent,
  renderAugmentedArtistsNames,
} from '../../utils/artists';
import { framedByDefault } from '../../utils/frames';
import { getArtistTag } from '../../utils/getArtistTag';
import { GlossaryLink } from '../../utils/glossary';
import {
  shopifyAdminIdToGraphqlId,
  shopifyGraphqlIdToAdminId,
} from '../../utils/shopifyIds';
import { storyblokProductReleaseType } from '../../utils/storyblokReleaseType';
import { NavigationStateContext } from '../Containers/NavigationStateProvider/NavigationStateProvider';
import StickyHeader from '../Containers/StickyHeader';
import { ArtistExclusive } from '../Content/ArtistExclusive/ArtistExclusive';
import { ArtistSubscription } from '../Content/ArtistSubscription/ArtistSubscription';
import MetaTags from '../Content/MetaTags';
import MultiMediaCarousel from '../Content/MultiMediaCarousel/MultiMediaCarousel';
import ProductAbstract from '../Content/ProductAbstract';
import ProductLd from '../Content/ProductLD';
import { ProductRecommendations } from '../Content/ProductRecommendations';
import ProductsCarousel from '../Content/ProductsCarousel';
import { Filter } from '../Content/ProductsCarousel/ProductsCarousel';
import { ContentBlockVideo } from '../ContentBlockVideo';
import { Image } from '../Image';
import Breadcrumbs from '../Layout/Breadcrumbs';
import PageSection from '../Layout/PageSection';
import { ProductPrice } from '../ProductPrice/ProductPrice';
import { ProductStickyButtons } from '../ProductStickyButtons/ProductStickyButtons';
import ProductTile from '../ProductTile';
import ScrollAnchor from '../ScrollAnchor';
import { ShareButtons } from '../Share/ShareButtons';

type Props = {
  slug: string;
  blok: ProductType;
  draw?: DrawByProductId;
  drawGateway: DrawGateway;
  aaProductsGateway: ProductGateway;
  augmentedArtists: AugmentedArtistCard[];
  productDetails: ProductPurchaseDetails | null;
  productGlossaryTerms: GlossaryLink[];
  recommendations: MultiCurrencyProduct[];
  reviews: GeneratedReview[];
};

type SeriesStory = {
  slug: string;
  full_slug: string;
  content: SeriesStoryblok;
};

const Product: React.FC<Props> = ({
  aaProductsGateway,
  augmentedArtists,
  blok,
  draw,
  drawGateway,
  productDetails,
  recommendations,
  reviews,
}) => {
  if (blok.component === 'product_v2') {
    return null;
  }

  const { t } = useTranslation([
    'product',
    'artists',
    'common',
    'orderDetails',
  ]);
  const releaseCollectionGalleryRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLAnchorElement>(null);

  const purchasingController = usePurchase();

  const drawController = useDraw({
    draw,
    product: blok,
    purchasingController,
    drawGateway,
  });

  const drawStage = useDrawStage(drawController.draw);
  const isPreOrder = useIsPreOrder(drawController.draw);

  const analytics = useMemo(
    () => ({
      context: 'product page',
      page: WORK_PAGE_TYPE,
      ...transformProductToEventObject(
        getStandardProductProperties(
          blok,
          purchasingController,
          drawStage,
          isPreOrder,
        ),
      ),
    }),
    [blok, purchasingController, drawStage, isPreOrder],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(analytics);

  const highlightVideo = blok.highlightVideo?.map((blok) => {
    return {
      ...blok,
      analytics: {
        ...analytics,
        context: 'product page highlight',
      },
    };
  });

  const { navigationState } = useContext(NavigationStateContext);

  const [framingDrawerVisible, setFramingDrawerVisible] = useState(false);

  const artists = (() => {
    if (!blok.productArtist) return [];

    return blok.productArtist.map((a: ResolvedRelatedArtist) => a.artist);
  })();

  const relatedSeries = (
    blok.relatedSeries?.[0] as { series?: SeriesStory } | undefined
  )?.series;

  // These are resolved Storyblok product stories, whose top-level `id` is the
  // numeric Storyblok story id. The Shopify (admin) id lives at content.id[0].id,
  // so we build graphql ids from that and fetch the real products to render.
  const relatedSeriesProductStories =
    (relatedSeries?.content.series?.[0]?.products as
      | ProductTypeObject[]
      | undefined) ?? [];

  const relatedSeriesProductIds = relatedSeriesProductStories.flatMap(
    (product) =>
      product?.content?.id?.[0]?.id &&
      product.content.id[0].id !== blok.id[0].id
        ? [shopifyAdminIdToGraphqlId(product.content.id[0].id)]
        : [],
  );

  const { products: relatedSeriesProducts } = useProducts(
    relatedSeriesProductIds,
  );

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    relatedSeriesProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  usePageViewAnalytics(blok, purchasingController, draw);

  const navOptions =
    navigationState && navigationState.type === 'PRODUCT'
      ? navigationState
      : undefined;

  const breadcrumbCount = relatedSeries
    ? relatedSeriesProductStories.length
    : navOptions
      ? navOptions.count
      : blok.worksCount;

  const breadcrumbIndexHref = relatedSeries
    ? pathFor({ page: 'SERIES', slug: relatedSeries.slug })
    : navOptions
      ? {
          pathname: pathFor({
            page: 'WORKS_INDEX',
            pageNumber: navOptions.page,
          }),
          query: {
            medium: navOptions.mediums,
            order: navOptions.order,
            q: navOptions.query,
            availability: navOptions.availability,
            release: navOptions.release,
            finish: navOptions.finish,
          },
        }
      : pathFor({ page: 'WORKS_INDEX' });

  const breadcrumbHref = navOptions ? undefined : breadcrumbIndexHref;

  const breadcrumbLabel = relatedSeries
    ? t('breadcrumbsSeries', { count: breadcrumbCount })
    : navOptions
      ? t('common:backToResults', { count: navOptions.count })
      : t('breadcrumbs', { count: breadcrumbCount });

  const productImageGalleryRef = useRef<HTMLAnchorElement>(null);
  const scrollToProductImageGallery = () => {
    productImageGalleryRef.current?.scrollIntoView({
      behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
    });
  };
  const [resetImageGallery, setResetImageGallery] = useState(0);

  const images = useMemo(() => {
    if (!purchasingController.frame) {
      return blok.images;
    }

    setResetImageGallery((prev) => prev + 1);
    scrollToProductImageGallery();

    // There is a convention followed by the content team that the first image
    // on the frame product is a frame by itself, and the second one is
    // a composite image of a product in a selected frame.
    return purchasingController.frame?.images.length > 1
      ? [
          {
            component: 'image' as const,
            image: {
              id: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
              alt: purchasingController.frame.title,
              copyright: '',
              filename: purchasingController.frame.images[1].src,
              name: '',
              title: '',
              fieldtype: '',
              focus: undefined,
            },
            showCopyright: false,
            _uid: purchasingController.frame.variant.consolidatedVariants[0]
              .shopifyFrameVariantId,
          },
          ...(blok.images?.slice(1) ?? []),
        ]
      : blok.images;
  }, [purchasingController.frame, blok.images]);

  const ctaRef = useRef<HTMLAnchorElement>(null);

  const [productState, setProductState] = useState<PurchasingState>('UNKNOWN');
  const releaseType = storyblokProductReleaseType(blok);
  const productIsArtistBag = productDetails?.tags
    .map((t) => t.toLowerCase())
    .includes('artist bag');

  useEffect(() => {
    if (purchasingController.product) {
      setProductState(
        presentedPurchasingState(
          purchasingState({
            releaseType: blok.releaseType,
            closingDate: blok.closingDate,
            available: purchasingController.product.available,
            tags: purchasingController.product.tags,
          }),
        ),
      );
    }
  }, [blok, purchasingController]);

  return (
    <AnalyticsObjectProvider name="product page" properties={analytics}>
      {() => (
        <>
          <ProductLd product={getProductLd(blok, purchasingController)} />
          {blok.excludeFromIndex && <MetaTags robots="noindex" />}
          {/* @ts-expect-error require storyblok-react upgrade */}
          <SbEditable content={blok}>
            <div>
              <Breadcrumbs
                href={breadcrumbHref}
                label={breadcrumbLabel}
                onClick={() => {
                  TrackLinkClicked({
                    page: WORK_PAGE_TYPE,
                    context: 'breadcrumbs',
                    title: breadcrumbLabel,
                    url: navOptions
                      ? pathFor({
                          page: 'WORKS_INDEX',
                          pageNumber: navOptions.page,
                        })
                      : (breadcrumbHref as string),
                    isExternal: false,
                    productTitle: blok.title,
                  });
                }}
              />

              {productState !== 'SOLD_OUT' && purchasingController.product ? (
                <StickyHeader ctaRef={heroImageRef}>
                  <div className="flex w-full items-center space-x-3">
                    <div className="h-12 w-12 shrink-0 rounded-sm">
                      <Image
                        alt={blok.thumbnail.alt}
                        focus={blok.thumbnail.focus}
                        shape="rounded"
                        src={blok.thumbnail.filename}
                        width={48}
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="truncate">{blok.title}</p>
                      <p className="text-neutral-4">
                        <ProductPrice
                          price={purchasingController.product.price}
                        />
                      </p>
                    </div>
                    <ProductStickyButtons
                      anchorRef={ctaRef}
                      draw={drawController.draw}
                      product={blok}
                      productRef={getProductRef(purchasingController)}
                      productState={productState}
                      productStatus={getProductStatus(
                        blok,
                        purchasingController,
                      )}
                      releaseType={releaseType}
                    />
                  </div>
                </StickyHeader>
              ) : null}

              <main className="mb-9 mt-0 space-y-9 md:my-12 md:space-y-12">
                <PageSection fullWidth>
                  <div className="md:px-[var(--website-absolute-padding)]">
                    <div className="grid-section gap-y-6">
                      <div className="col-span-12 md:col-span-6">
                        <ScrollAnchor
                          ref={productImageGalleryRef}
                          id="productImageGallery"
                          offset={-72}
                        />
                        {images?.length > 0 && (
                          <div className="sticky top-0 sm:top-30">
                            <ProductImageGallery
                              heroImageRef={heroImageRef}
                              images={images}
                              onClick={(properties: MultiMediaClickOptions) => {
                                TrackMultiMediaClick({
                                  ...properties,
                                  page: WORK_PAGE_TYPE,
                                  pageTitle: blok.title,
                                  context: 'product image gallery',
                                });
                              }}
                              resetTrigger={resetImageGallery}
                            />
                            <div className="splide-skeleton">
                              {images.slice(0, 1).map((media) => {
                                const image =
                                  media.component === 'Video'
                                    ? media.coverImage
                                    : media.image;
                                const sizes = {
                                  sm: { ratio: '4/5', span: 12 },
                                  md: { ratio: '1/1', span: 6 },
                                  lg: { ratio: '1/1', span: 6 },
                                } as ImageSizes;

                                return (
                                  <Image
                                    key={image.id}
                                    alt={image.alt}
                                    eagerLoading
                                    focus={image.focus}
                                    hoverEffect
                                    shape="rounded"
                                    size={sizes}
                                    src={image.filename}
                                  />
                                );
                              })}
                              <div className="splide__pagination w-full gap-2 pt-4 md:!hidden">
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                                <div className="splide__pagination__page block h-1.5 w-1.5 animate-pulse rounded-full bg-neutral-2" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <div className="sticky top-0 mx-auto w-full max-w-md space-y-9 px-5 sm:top-30 md:space-y-12 md:px-0">
                          <ProductAbstract
                            ref={ctaRef}
                            analytics={analytics}
                            draw={drawController.draw}
                            framingDrawerVisible={framingDrawerVisible}
                            product={blok}
                            productsGateway={aaProductsGateway}
                            purchasing={purchasingController}
                            releaseCollectionGalleryRef={
                              releaseCollectionGalleryRef
                            }
                            setFramingDrawerVisible={setFramingDrawerVisible}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </PageSection>

                {highlightVideo?.length > 0 && (
                  <PageSection className="hidden md:block">
                    <ContentBlockVideo blok={highlightVideo[0]} />
                  </PageSection>
                )}

                {blok.fromTheArtistContainer &&
                  blok.fromTheArtistContainer.length > 0 && (
                    <PageSection fullWidth>
                      <ArtistExclusive
                        analytics={{
                          page: WORK_PAGE_TYPE,
                          pageTitle: blok.title,
                          context: 'from the artist',
                        }}
                        items={blok.fromTheArtistContainer}
                        title={
                          artists.length > 1
                            ? t('product:fromTheArtists')
                            : t('product:fromTheArtist')
                        }
                      />
                    </PageSection>
                  )}

                {blok.behindTheScenesContainer &&
                  blok.behindTheScenesContainer.length > 0 && (
                    <PageSection fullWidth>
                      <MultiMediaCarousel
                        analytics={{
                          page: WORK_PAGE_TYPE,
                          pageTitle: blok.title,
                          context: t('product:behindTheScenes'),
                        }}
                        config={blok.behindTheScenesContainer[0]}
                        headline={t('product:behindTheScenes')}
                        imageSizes={{
                          sm: { ratio: '4/3', span: 12 },
                          md: { ratio: '4/3', span: 6 },
                          lg: { ratio: '4/3', span: 6 },
                        }}
                        perPage={2}
                        showCaption={false}
                      />
                    </PageSection>
                  )}

                {relatedSeriesProducts.length > 0 && (
                  <PageSection>
                    <h2 className="neutral-4 relative z-20 mb-4 text-md md:text-lg">
                      {t('seriesTitle')}
                    </h2>

                    <div className="grid-section gap-y-6">
                      {relatedSeriesProducts.map((product, index) => (
                        <article
                          key={product.id}
                          className="col-span-6 flex flex-col gap-y-3 md:col-span-4 lg:col-span-3"
                        >
                          <ProductTile
                            analytics={
                              analytics && {
                                page: analytics.page,
                                context: t('seriesTitle'),
                                position: index,
                              }
                            }
                            isSubscribed={checkIsSubscribed(
                              shopifyGraphqlIdToAdminId(product.id),
                              GeneratedSubscriptionType.Product,
                            )}
                            onSubscribe={() => {
                              refetch();
                            }}
                            product={product}
                          />
                        </article>
                      ))}
                    </div>
                  </PageSection>
                )}

                {blok.releaseCollectionGallery?.length > 0 && (
                  <div
                    ref={releaseCollectionGalleryRef}
                    className="before:width-full before:invisible before:-z-90 before:-mt-40 before:block before:h-40 before:content-['']"
                  >
                    <PageSection>
                      <section className="space-y-4">
                        <Heading level="h2">
                          {t('releaseCollectionGalleryHeading')}
                        </Heading>
                        <GridGallery images={blok.releaseCollectionGallery} />
                      </section>
                    </PageSection>
                  </div>
                )}
                {blok.fromTheExpertsContainer &&
                  blok.fromTheExpertsContainer.length > 0 && (
                    <PageSection fullWidth>
                      <MultiMediaCarousel
                        analytics={{
                          page: WORK_PAGE_TYPE,
                          pageTitle: blok.title,
                          context: t('product:fromTheExperts'),
                        }}
                        config={blok.fromTheExpertsContainer[0]}
                        headline={t('product:fromTheExperts')}
                        imageSizes={{
                          sm: { ratio: '4/3', span: 12 },
                          md: { ratio: '4/3', span: 6 },
                          lg: { ratio: '4/3', span: 6 },
                        }}
                        perPage={2}
                        showCaption={false}
                      />
                    </PageSection>
                  )}
                {artists?.length > 0 && (
                  <PageSection>
                    <Grid className="gap-y-6" columns={4}>
                      {artists.map((artist) => {
                        const augmentedArtist = augmentedArtists.find(
                          (value) => value.slug === artist.slug,
                        );

                        if (!augmentedArtist) return null;

                        return (
                          <Card key={artist.id}>
                            <Tile className="group gap-4" direction="column">
                              <TileMedia
                                className="rounded-none"
                                overlay={
                                  <>
                                    {artist.internalId ? (
                                      <ArtistSubscription
                                        buttonLocation="product page"
                                        internalId={artist.internalId}
                                        name={artist?.name}
                                        thumbnail={
                                          artist.content.thumbnail.filename
                                        }
                                      />
                                    ) : null}
                                  </>
                                }
                              >
                                <Image
                                  src={artist.content.thumbnail.filename}
                                  focus={artist.content.thumbnail.focus}
                                  alt={artist.content.thumbnail.alt}
                                  hoverEffect
                                  size={{
                                    sm: { ratio: '4/3', span: 12 },
                                    md: { ratio: '4/3', span: 6 },
                                    lg: { ratio: '4/3', span: 4 },
                                  }}
                                />
                              </TileMedia>

                              <TileContent
                                className="w-full px-4 pb-4"
                                direction="column"
                              >
                                <TileTitle>
                                  <Link
                                    href={`/${artist.full_slug}`}
                                    onClick={() => {
                                      void TrackEvent(
                                        `${analytics.page} artist tile link clicked`,
                                        {
                                          ...analytics,
                                          'link title': `${artist.name}`,
                                          'link url': `/${artist.full_slug}`,
                                          'is external': false,
                                        },
                                      );
                                    }}
                                    prefetch={false}
                                    className="after:absolute after:inset-0 after:block after:content-['']"
                                  >
                                    {artist.name}
                                  </Link>
                                </TileTitle>
                                <TileText>
                                  {getArtistTileOverline(
                                    augmentedArtist.totalWorks,
                                    augmentedArtist.hideCollaborations,
                                    t,
                                  )}
                                </TileText>

                                {isStoryblokRichTextPresent(
                                  artist.content.practiceOverview,
                                ) ? (
                                  <TileText
                                    className="mt-3 line-clamp-3"
                                    variant="muted"
                                  >
                                    {render(
                                      getFirstStoryblokParagraph(
                                        artist.content.practiceOverview,
                                      ),
                                      {
                                        markResolvers: {
                                          link: (children) => <>{children}</>,
                                        },
                                        nodeResolvers: {
                                          paragraph: (children) => (
                                            <>{children}</>
                                          ),
                                        },
                                      },
                                    )}
                                  </TileText>
                                ) : null}
                              </TileContent>
                            </Tile>
                          </Card>
                        );
                      })}
                    </Grid>
                  </PageSection>
                )}
                <PageSection>
                  <div className="flex flex-col gap-y-4">
                    <div className="flex flex-col text-center">
                      <p className="font-bold">
                        Who else should know about this work?
                      </p>
                      <p>Help us spread the word</p>
                    </div>

                    <ShareButtons
                      className="flex items-center justify-center gap-2"
                      onClick={(platform) =>
                        analyticsEmitter('share', platform)
                      }
                      text={blok.title}
                      utmCampaign="product-page"
                    />
                  </div>
                </PageSection>
                {artists.length > 0 && (
                  <ProductsCarousel
                    analytics={{
                      page: WORK_PAGE_TYPE,
                      context: t('product:moreWorksHeading', {
                        artist: renderAugmentedArtistsNames(augmentedArtists),
                      }),
                    }}
                    filter={{
                      operation: 'OR',
                      items: artists.map((a) => getArtistTag(a)),
                    }}
                    heading={t(
                      artists.length > 1
                        ? 'product:moreWorksHeadingPlural'
                        : 'product:moreWorksHeading',
                    )}
                    numberOfProducts={100}
                    order="DESC"
                    productToExclude={
                      blok.id?.[0]?.id
                        ? shopifyAdminIdToGraphqlId(blok.id[0].id)
                        : void 0
                    }
                  />
                )}
                {reviews.length > 3 && (
                  <div className="wrapper-website">
                    <Divider className="my-9 md:my-12" />
                  </div>
                )}
                {reviews.length > 3 && (
                  <PageSection fullWidth>
                    <Carousel
                      headline={t('reviews.title')}
                      items={reviews.map((review) => (
                        <ReviewCard key={review.id} review={review} />
                      ))}
                      onSlide={(index) => {
                        TrackCarouselSlide({
                          page: 'product',
                          context: 'reviews',
                          title: t('reviews.title'),
                          position: index,
                          carouselLength: reviews.length,
                        });
                      }}
                      perPage={4}
                    />
                  </PageSection>
                )}
                <div className="wrapper-website">
                  <Divider className="my-9 md:my-12" />
                </div>
                {productIsArtistBag ? (
                  <PageSection fullWidth>
                    <ProductsCarousel
                      analytics={{
                        page: WORK_PAGE_TYPE,
                        context: 'artist bag recommendations',
                      }}
                      filter={createArtistBagsCarouselFilter(
                        artists.map((a) => getArtistTag(a)),
                      )}
                      heading={t('common:recommendations.titles.default')}
                      numberOfProducts={5}
                      order="DESC"
                      productToExclude={
                        blok.id?.[0]?.id
                          ? shopifyAdminIdToGraphqlId(blok.id[0].id)
                          : void 0
                      }
                    />
                  </PageSection>
                ) : (
                  <PageSection>
                    <ProductRecommendations
                      analytics={{
                        page: WORK_PAGE_TYPE,
                        context: 'more works',
                      }}
                      mediumTag={
                        blok.medium?.includes('print')
                          ? PRODUCT_TAG_MEDIUM_PRINT
                          : blok.medium?.includes('sculpture')
                            ? PRODUCT_TAG_MEDIUM_SCULPTURE
                            : blok.medium?.includes('collectible')
                              ? PRODUCT_TAG_MEDIUM_COLLECTIBLE
                              : blok.medium?.includes('nft')
                                ? PRODUCT_TAG_MEDIUM_NFT
                                : blok.medium?.includes('studioworks')
                                  ? PRODUCT_TAG_MEDIUM_STUDIO_WORKS
                                  : undefined
                      }
                      recommendations={recommendations}
                    />
                  </PageSection>
                )}
              </main>
            </div>
          </SbEditable>
        </>
      )}
    </AnalyticsObjectProvider>
  );
};

export default Product;

function createArtistBagsCarouselFilter(artistNames: string[]): Filter {
  if (artistNames.length === 0) {
    return {
      operation: 'AND',
      exclude: false,
      items: ['Artist bag'],
    };
  }

  return {
    operation: 'AND',
    items: [
      'Artist bag',
      {
        operation: 'AND',
        exclude: true,
        items: artistNames,
      },
    ],
  };
}

const usePageViewAnalytics = (
  product: ProductType,
  purchasingController: ProductPurchasingController<any>,
  draw?: DrawByProductId,
) => {
  const drawStage = useDrawStage(draw);
  const productPreOrderAvailable = useIsPreOrder(draw);
  const [hasSent, setHasSent] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (purchasingController.loading) return;
    if (hasSent === product.id[0].id) return;

    TrackWorkPageView({
      drawStage,
      page: WORK_PAGE_TYPE,
      product,
      productPreOrderAvailable,
      productCanBeFramed: !!purchasingController.frameOptions?.length,
      frameIncluded: framedByDefault(
        purchasingController.product.framingOffered,
      ),
      frameUpgradeAvailable: Boolean(purchasingController.frameOptions),
      productStatus: getProductStatus(product, purchasingController),
      price: purchasingController.product?.price,
    });

    product.id && setHasSent(product.id[0].id);
  }, [
    purchasingController.loading,
    purchasingController.product,
    purchasingController,
    hasSent,
    product,
    drawStage,
    productPreOrderAvailable,
  ]);
};

const getProductLd = (
  product: ProductType,
  purchasingController: ProductPurchasingController<any>,
): ProductStructure => {
  const router = useRouter();
  return {
    name: product.title,
    image: product.thumbnail.filename,
    primaryImageOfPage: product.thumbnail.filename,
    description: product.seo.description,
    artMedium: product.medium,
    artistName: renderArtistsNamesFromPageContent(product.productArtist),
    ...(product.productArtist &&
      product.productArtist.length > 0 && {
        artistUrl: new URL(
          (product.productArtist[0] as any).artist.full_slug,
          process.env.NEXT_PUBLIC_HOSTNAME,
        ).toString(),
      }),
    pricesAndAvailability: product.pricesAndAvailability,
    availability: getProductStatus(product, purchasingController),
    url: new URL(router.asPath, process.env.NEXT_PUBLIC_HOSTNAME).toString(),
  };
};
